import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Package, Trash2, Download, Upload } from 'lucide-react';
import { fmt } from '../../lib/accountingStore';
import { apiProducts } from '../../lib/api';
import { toCSV, downloadFile, parseCSV, readFileAsText } from '../../lib/importExport';
import { toast } from 'sonner';

export default function Products() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { apiProducts.list().then((d) => setProducts(d || [])).catch(() => setProducts([])); }, []);
  useEffect(() => { load(); }, [load]);
  const onDelete = async (id) => { try { await apiProducts.remove(id); load(); toast.success('Removed'); } catch { toast.error('Failed'); } };
  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-products">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Products &amp; Services</h2><p className="text-slate-500 text-sm">Items you sell on invoices and estimates.</p></div>
        <Button onClick={() => setOpen(true)} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="new-product-btn"><Plus className="w-4 h-4" />New Item</Button>
      </div>
      <div className="flex gap-2 -mt-2">
        <label className="inline-flex items-center gap-1.5 px-3 h-9 bg-white border border-slate-200 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer"><Upload className="w-3.5 h-3.5" />Import CSV
          <input type="file" accept=".csv" className="hidden" onChange={async (e) => {
            const f = e.target.files?.[0]; if (!f) return;
            const parsed = parseCSV(await readFileAsText(f));
            const docs = parsed.map((r, i) => ({ id: r.ID || `P-${Date.now()}-${i}`, name: r.Name || '', type: r.Type || 'Service', price: Number(r.Price) || 0, account: r.Account || 'Service Revenue' }));
            try { await apiProducts.bulk(docs); load(); toast.success(`Imported ${docs.length} items`); } catch { toast.error('Import failed'); }
            e.target.value = '';
          }} /></label>
        <Button size="sm" variant="outline" onClick={() => { downloadFile('xpertpests-products.csv', toCSV(products, [{ key: 'id', header: 'ID' }, { key: 'name', header: 'Name' }, { key: 'type', header: 'Type' }, { key: 'price', header: 'Price' }, { key: 'account', header: 'Account' }])); toast.success('Exported products.csv'); }} className="gap-1.5 h-9"><Download className="w-3.5 h-3.5" />Export CSV</Button>
      </div>
      {products.length === 0 ? (
        <Card className="p-10 text-center border-slate-200"><Package className="w-8 h-8 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No products or services yet. Add the items you bill for.</p></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <Card key={p.id} className="pp-lift p-5 border-slate-200">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-lg bg-[#8BC53F]/10 text-[#8BC53F] flex items-center justify-center"><Package className="w-5 h-5" /></div>
                <Button size="sm" variant="ghost" onClick={() => onDelete(p.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
              <div className="font-bold text-slate-900 mt-3">{p.name}</div>
              <Badge className="mt-1 bg-slate-100 text-slate-700 border-0">{p.type}</Badge>
              <div className="mt-3 flex justify-between items-end"><div><div className="text-xs text-slate-500">Income Account</div><div className="text-xs font-medium text-slate-700">{p.account}</div></div><div className="text-2xl font-extrabold tabular-nums text-slate-900">{fmt(p.price)}</div></div>
            </Card>
          ))}
        </div>
      )}
      <ProductDialog open={open} setOpen={setOpen} onSave={load} />
    </div>
  );
}

function ProductDialog({ open, setOpen, onSave }) {
  const [name, setName] = useState(''); const [price, setPrice] = useState(''); const [type, setType] = useState('Service');
  const submit = async () => {
    if (!name || !price) { toast.error('Name and price required'); return; }
    try { await apiProducts.create({ id: `P-${Date.now().toString(36)}`, name, type, price: Number(price), account: 'Service Revenue' }); onSave(); setOpen(false); setName(''); setPrice(''); toast.success('Item added'); } catch { toast.error('Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="product-dialog">
        <DialogHeader><DialogTitle>New Product / Service</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" placeholder="e.g. Annual Service Plan" /></div>
          <div className="grid grid-cols-2 gap-3"><div><Label>Type</Label><select className="mt-1.5 h-10 w-full rounded-md border border-slate-200 px-3 text-sm" value={type} onChange={(e) => setType(e.target.value)}><option>Service</option><option>Inventory</option><option>Non-Inventory</option></select></div><div><Label>Price</Label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1.5" /></div></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="product-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
